import React from "react";

import { isEmpty } from "lodash";

import FormRadioGroup from "../../../../components/Form/FormRadioGroup";
import BottomButton from "../../../../components/tailwind/BottomButton";
import { amountAsCurrency } from "../../../../services/currency";
import {
  getSavings,
  getWorstPrice,
  isBestPrice,
  priceLabel,
  priceIntervalText,
  getSavingsCash,
} from "../../../../services/Price";
import { Merchant as FullMerchant } from "../../../../types/Merchant";
import { Product, Price, PaymentPlanType } from "../../../../types/Product";
import { PageProgress } from "../../../../web-app/CatalogueLayout";
import MerchantHeader from "../shared/MerchantHeader";
import PaymentDates from "../shared/PaymentDates";

type Merchant = Pick<
  FullMerchant,
  | "avatar_url"
  | "full_name"
  | "username"
  | "has_ecommerce_store"
  | "currency_code"
>;

export type Props = {
  merchant: Merchant;
  subscription: Product;
  prices: Price[];
  selectedPrice: Price | null;
  savingsEnabled: boolean;

  inPreviewMode?: boolean;

  selectPrice: (price: Price) => void;
  //ToDo: rename onSelect to something that better describes that this happpens on click
  onSelect: () => void;
};

const SubscriptionSelect = ({
  merchant,
  subscription,
  prices,
  selectedPrice,
  savingsEnabled,
  inPreviewMode = false,
  selectPrice,
  onSelect,
}: Props) => {
  const worstPrice = getWorstPrice(prices);
  const isNotSubscription = prices.every(
    (x) => x.payment_plan_type !== PaymentPlanType.RECURRING
  );

  const currency = merchant.currency_code;

  const mostExpensivePlan = prices
    .slice()
    .sort((a, b) => b.amount - a.amount)[0];
  const cheapestPlanAmount = prices
    .map((x) => x.amount)
    .sort((a, b) => a - b)[0];

  function pillBackground(soldOut: boolean, isLowestCost: boolean) {
    if (soldOut) {
      return "bg-gray-200 text-gray-300";
    } else if (isLowestCost) {
      return "bg-primary-500 text-white";
    } else {
      return "bg-primary-100 text-primary-600";
    }
  }

  return (
    <div className="h-full w-full pt-3 flex flex-col">
      <div className="flex-grow flex flex-col">
        {!inPreviewMode && (
          <MerchantHeader merchant={merchant} avatarSize={40} />
        )}
        {/* TODO: simplify internal component as we don't need it for this :S.
        
        {!inPreviewMode && (
          <PageProgress
            username={merchant.username}
            hasFulfillmentOptions={false}
            isPaymentPlan={isNotSubscription}
            isSubscription
            cartItems={[{ ...subscription, hasIcon: false, quantity: 1 }]}
          />
        )} */}

        <span className="text-base text-gray-800 font-semibold pt-2 px-4 block">
          {subscription.name}
        </span>
        <span className="p-4 text-gray-500 text-sm">
          {subscription.short_description}
        </span>
        <span className="font-semibold text-gray-800 text-base px-4 pb-4 block">
          {isNotSubscription
            ? "Elige tu plan de pagos"
            : "Frecuencia de suscripción"}
        </span>
        <div className="px-2 pb-4">
          <FormRadioGroup hasMargin={false}>
            {/* prices sorted in BE */}
            {prices.map((price, index) => {
              //ToDo: extract to component
              const amountSavedCash = getSavingsCash(mostExpensivePlan, price);
              const amountSaved = getSavings(worstPrice, price);
              const hasSavings = isNotSubscription
                ? amountSavedCash > 0
                : amountSaved > 0;
              const isLowestCost = isNotSubscription
                ? price.amount === cheapestPlanAmount
                : isBestPrice(prices, price);
              const label = priceLabel(price, "Pagar");
              const isInstallmentPlan =
                price.payment_plan_type === PaymentPlanType.INSTALLMENT;
              const isOneOff =
                price.payment_plan_type === PaymentPlanType.ONE_OFF;
              const sold_out = price.sold_out || false;
              const showSavings = savingsEnabled && hasSavings;
              return (
                <FormRadioGroup.Item
                  key={index}
                  label={label}
                  labelNumberOfLines={null}
                  selected={price == selectedPrice}
                  alignTop
                  disableOpacity
                  disabled={sold_out}
                  onSelect={() => selectPrice(price)}
                  right={
                    <div className="flex flex-col">
                      <span
                        className={`text-xl text-right font-bold ${
                          sold_out && "text-gray-300"
                        }`}
                      >
                        {isInstallmentPlan
                          ? amountAsCurrency({
                              amount: price.amount / price.installments_count,
                              currency,
                            })
                          : amountAsCurrency({
                              amount: price.amount,
                              currency,
                            })}
                      </span>
                      {/* display unless it is a one-off payment */}
                      {!isOneOff && (
                        <div className="flex-none pt-1.5 text-xs text-right">
                          <span
                            className={`text-xs ${
                              sold_out ? "text-gray-300" : "text-gray-500"
                            }`}
                          >
                            {priceIntervalText(price)}
                          </span>
                        </div>
                      )}
                      {sold_out && (
                        <div className="flex-none pt-1.5 text-xs text-right">
                          <span className="text-gray-300 text-xs">agotado</span>
                        </div>
                      )}
                    </div>
                  }
                  description={
                    showSavings && (
                      <div className="pt-1.5 items-start flex-none">
                        <span
                          className={`rounded text-xs font-semibold p-1 ${pillBackground(
                            sold_out,
                            isLowestCost
                          )}`}
                        >
                          {isNotSubscription
                            ? `Ahorras ${amountAsCurrency({
                                amount: amountSavedCash,
                                currency,
                              })}`
                            : `Ahorras ${amountSaved}%`}
                        </span>
                      </div>
                    )
                  }
                >
                  {/* Only activates if there are future payments (use FakeAPI fixture in actions to test) */}
                  {price.installments !== undefined &&
                    price.installments.length > 0 && (
                      <PaymentDates currency={currency} price={price} />
                    )}
                </FormRadioGroup.Item>
              );
            })}
          </FormRadioGroup>
        </div>
      </div>
      {!inPreviewMode && (
        <BottomButton
          text="Continuar"
          disabled={isEmpty(selectedPrice)}
          onClick={onSelect}
        />
      )}
    </div>
  );
};

export default SubscriptionSelect;
